import stationDataConfigApi from '@/api/config/stationDataConfig'
import TableFooter from '@/components/common/Table/TableFooter'
import { useAuthContext } from '@/features/auth/context'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import {
  DATA_PAGINATION,
  Emitter,
  EtZone,
  Plant,
  SearchStation,
  SetupStation,
} from '@/types'
import { AreaType } from '@/types/areaZone'
import { FertigationSetup } from '@/types/fertigation'
import { Historical } from '@/types/historical'
import { HydraulicType } from '@/types/hydraulic'
import { SensorDetailImport } from '@/types/sensor'
import { Soil } from '@/types/soil'
import { TabContext, TabPanel, usePagination } from '@mui/lab'
import { SelectChangeEvent, Tab, Tabs } from '@mui/material'
import { Stack } from '@mui/system'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import AreaTable from '../components/AreaTable'
import EmitterTable from '../components/EmitterTable'
import EtReferenceTable from '../components/EtReferenceTable'
import EtZoneTable from '../components/EtZoneTable'
import FertigationTable from '../components/FertigationTable'
import HydraulicTable from '../components/HydraulicTable'
import PlantTable from '../components/PlantTable'
import SensorTable from '../components/SensorTable'
import SoilTable from '../components/SoilTable'
import StationTable from '../components/StationTable'
import classes from './styles.module.scss'

interface CommonDataTableProps {
  dataHydraulic?: DATA_PAGINATION<HydraulicType>
  dataFertigation?: DATA_PAGINATION<FertigationSetup>
  dataSensor?: DATA_PAGINATION<SensorDetailImport>
  dataEt?: DATA_PAGINATION<Historical>
  dataPlant?: DATA_PAGINATION<Plant>
  dataEmitter?: DATA_PAGINATION<Emitter>
  dataAreas?: AreaType[]
  totalCountAreas?: number
  dataSoil?: DATA_PAGINATION<Soil>
  dataEtZone?: DATA_PAGINATION<EtZone>
}

const CommonDataTable: FC<CommonDataTableProps> = ({
  dataSensor,
  dataHydraulic,
  dataFertigation,
  dataEt,
  dataPlant,
  dataEmitter,
  dataAreas,
  dataSoil,
  dataEtZone,
  totalCountAreas,
}) => {
  const themeMode = useSelector(selectTheme)

  const [tab, setTab] = useState<string>('0')
  const { interfaceId } = useAuthContext()

  const [searchParams, setSearchParams] = useState<SearchStation>({
    PageSize: 10,
    PageIndex: 1,
    interfaceId:Number(interfaceId),
  })

  const [station, setStation] = useState<DATA_PAGINATION<SetupStation>>()

  useEffect(() => {
    const fetchData = async () => {
      const _data = await stationDataConfigApi.getListWithDetail(
        Number(interfaceId),
        searchParams,
      )
      const _dataStations = _data?.data || []
      setStation(_dataStations)
    }
    fetchData()
    //eslint-disable-ne
  }, [searchParams])

  const [currentPage, setCurrentPage] = useState(1)

  const handleChangeTab = (_: any, newTab: string) => {
    setTab(newTab)
  }

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const page = parseInt(event.target.value, 10)
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      PageSize: page,
      PageIndex: 1,
    })
  }

  const { items } = usePagination({
    count: station?.totalPage,
    page: currentPage,
    onChange(_event: any, page: any) {
      setCurrentPage(page)
      setSearchParams({
        ...searchParams,
        // PageSize: 10,
        PageIndex: page,
      })
    },
  })

  const render = (tab: string) => {
    switch (tab) {
      case '0':
        return (
          <TabPanel value={'0'} className={classes.tabCommon}>
            <EtReferenceTable data={dataEt} />
          </TabPanel>
        )

      case '1':
        return (
          <TabPanel value={'1'} className={classes.tabCommon}>
            <PlantTable data={dataPlant} />
          </TabPanel>
        )
      case '2':
        return (
          <TabPanel value={'2'} className={classes.tabCommon}>
            <SoilTable data={dataSoil} />
          </TabPanel>
        )
      case '3':
        return (
          <TabPanel value={'3'} className={classes.tabCommon}>
            <EmitterTable data={dataEmitter} />
          </TabPanel>
        )
      case '4':
        return (
          <TabPanel value={'4'} className={classes.tabCommon}>
            <EtZoneTable data={dataEtZone} />
          </TabPanel>
        )
      case '5':
        return (
          <TabPanel value={'5'} className={classes.tabCommon}>
            <AreaTable data={dataAreas} totalCount={totalCountAreas} />
          </TabPanel>
        )

      case '6':
        return (
          <TabPanel value={'6'} className={classes.tabStation}>
            <HydraulicTable rows={dataHydraulic} />
          </TabPanel>
        )
      case '7':
        return (
          <TabPanel value={'7'} className={classes.tabStation}>
            <SensorTable data={dataSensor} />
          </TabPanel>
        )
      case '8':
        return (
          <TabPanel value={'8'} className={classes.tabStation}>
            <FertigationTable data={dataFertigation} />
          </TabPanel>
        )
      case '9':
        return (
          <TabPanel value={'9'} className={classes.tabStation}>
            <Stack flexGrow="1" height="100%">
              {station && <StationTable data={station} />}
              <TableFooter
                page={station?.page}
                totalCount={station?.totalCount}
                totalPage={station?.totalPage}
                rowsPerPage={station?.size ?? 0}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                items={items}
              />
            </Stack>
          </TabPanel>
        )
    }
  }
  return (
    <Stack flexGrow="1">
      <Stack className={classes.wrapper}>
        <TabContext value={tab}>
          <Tabs
            value={tab}
            onChange={handleChangeTab}
            textColor="inherit"
            sx={{
              minHeight: 'unset',
              height: '80px',
              padding: '10px',
              bgcolor: 'background.overrideDefault',
              width: '100%',
              display: 'flex', // Enable flex container
              justifyContent: 'space-between', // Distribute space between tabs
              alignItems: 'center', // Align items vertically
              '.MuiTabs-flexContainer': {
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  width: '4px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#A5A5A5',
                  borderRadius: '3px',
                },
              },
              '.MuiTab-root': {
                p: 0,
                flex: 1,
                height: '60px',
                minHeight: '40px',
                fontWeight: 700,
                lineHeight: '14px',
                fontSize: '16px',
                color: 'text.main',
                '&.Mui-selected': {
                  color: 'primary.main',
                  borderBottom: {
                    ...(themeMode === mode_type.dark
                      ? { borderBottom: '2px solid #e6eded' }
                      : { borderBottom: '2px solid #004744' }),
                  },
                },
                '&:not(:last-child)': {
                  marginRight: '10px',
                },
              },
              '.MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab value={'0'} label="ET Reference" />
            <Tab value={'1'} label="Plant" />
            <Tab value={'2'} label="Soil" />
            <Tab value={'3'} label="Emitter" />
            <Tab value={'4'} label="Micro Climate" />
            <Tab value={'5'} label="Areas & Sub-Areas" />
            <Tab value={'6'} label="Hydraulic" />
            <Tab value={'7'} label="Sensor" />
            <Tab value={'8'} label="Fertigation" />
            <Tab value={'9'} label="Station" />
          </Tabs>
        </TabContext>
        <TabContext value={tab}>
          <Stack flexGrow={1} bgcolor="background.default">
            {render(tab)}
          </Stack>
        </TabContext>
      </Stack>
    </Stack>
  )
}

export default CommonDataTable
